import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Filter, Search, Download, Eye, Edit, Trash2, 
  Clock, Calendar, User, Building2, DollarSign, Package,
  CheckCircle2, AlertTriangle, XCircle, ArrowRight, 
  ChevronDown, ChevronUp, FileText, Users, Settings,
  TrendingUp, TrendingDown, BarChart2, RefreshCw,
  ShoppingCart, Handshake, FileCheck, Award, Star,
  Phone, Mail, Globe, MapPin, Truck, Shield
} from 'lucide-react';

const ProcurementTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('vendors');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const subTabs = [
    { id: 'vendors', label: 'Vendor Registry', icon: Users },
    { id: 'rfqs', label: 'RFQs', icon: FileCheck },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
    { id: 'contracts', label: 'Contracts', icon: Handshake }
  ];

  // Comprehensive mock data
  const vendors = [
    {
      id: 'V001',
      name: 'Tech Solutions Ltd',
      category: 'Technology',
      contactPerson: 'Ahmed Al-Rashid',
      email: 'ahmed@techsolutions.com',
      phone: '+966-50-123-4567',
      website: 'www.techsolutions.com',
      address: 'Riyadh, Saudi Arabia',
      rating: 4.8,
      status: 'active',
      totalSpend: 1250000,
      lastOrder: '2024-03-15',
      performance: {
        onTimeDelivery: 95,
        qualityScore: 92,
        costCompetitiveness: 88,
        communication: 90
      },
      contracts: 3,
      activePOs: 5
    },
    {
      id: 'V002',
      name: 'Global Equipment Co.',
      category: 'Equipment',
      contactPerson: 'Sarah Johnson',
      email: 'sarah@globalequipment.com',
      phone: '+966-50-234-5678',
      website: 'www.globalequipment.com',
      address: 'Jeddah, Saudi Arabia',
      rating: 4.5,
      status: 'active',
      totalSpend: 890000,
      lastOrder: '2024-03-10',
      performance: {
        onTimeDelivery: 88,
        qualityScore: 85,
        costCompetitiveness: 92,
        communication: 87
      },
      contracts: 2,
      activePOs: 3
    },
    {
      id: 'V003',
      name: 'Office Supplies Pro',
      category: 'Office Supplies',
      contactPerson: 'Mohammed Ali',
      email: 'mohammed@officesupplies.com',
      phone: '+966-50-345-6789',
      website: 'www.officesupplies.com',
      address: 'Dammam, Saudi Arabia',
      rating: 4.2,
      status: 'active',
      totalSpend: 450000,
      lastOrder: '2024-03-18',
      performance: {
        onTimeDelivery: 92,
        qualityScore: 88,
        costCompetitiveness: 85,
        communication: 94
      },
      contracts: 1,
      activePOs: 2
    },
    {
      id: 'V004',
      name: 'Safety Gear International',
      category: 'Safety Equipment',
      contactPerson: 'Fatima Hassan',
      email: 'fatima@safetygear.com',
      phone: '+966-50-456-7890',
      website: 'www.safetygear.com',
      address: 'Makkah, Saudi Arabia',
      rating: 4.7,
      status: 'pending',
      totalSpend: 320000,
      lastOrder: '2024-03-05',
      performance: {
        onTimeDelivery: 90,
        qualityScore: 95,
        costCompetitiveness: 78,
        communication: 89
      },
      contracts: 1,
      activePOs: 1
    }
  ];

  const rfqs = [
    {
      id: 'RFQ-2024-001',
      title: 'IT Infrastructure Equipment',
      category: 'Technology',
      status: 'open',
      budget: 500000,
      responses: 8,
      deadline: '2024-03-25',
      createdBy: 'IT Department',
      createdDate: '2024-03-10',
      description: 'Complete IT infrastructure setup for new office location'
    },
    {
      id: 'RFQ-2024-002',
      title: 'Kitchen Equipment & Appliances',
      category: 'Equipment',
      status: 'evaluating',
      budget: 750000,
      responses: 12,
      deadline: '2024-03-20',
      createdBy: 'Operations',
      createdDate: '2024-03-08',
      description: 'Commercial kitchen equipment for restaurant expansion'
    },
    {
      id: 'RFQ-2024-003',
      title: 'Office Furniture & Interior Design',
      category: 'Office Supplies',
      status: 'awarded',
      budget: 300000,
      responses: 6,
      deadline: '2024-03-15',
      createdBy: 'HR Department',
      createdDate: '2024-03-05',
      description: 'Modern office furniture and interior design services'
    }
  ];

  const purchaseOrders = [
    {
      id: 'PO-2024-001',
      vendor: 'Tech Solutions Ltd',
      totalAmount: 125000,
      status: 'approved',
      orderDate: '2024-03-15',
      expectedDelivery: '2024-03-25',
      items: 8,
      category: 'Technology'
    },
    {
      id: 'PO-2024-002',
      vendor: 'Global Equipment Co.',
      totalAmount: 89000,
      status: 'in-progress',
      orderDate: '2024-03-12',
      expectedDelivery: '2024-03-22',
      items: 5,
      category: 'Equipment'
    },
    {
      id: 'PO-2024-003',
      vendor: 'Office Supplies Pro',
      totalAmount: 45000,
      status: 'delivered',
      orderDate: '2024-03-08',
      expectedDelivery: '2024-03-18',
      items: 12,
      category: 'Office Supplies'
    }
  ];

  const contracts = [
    {
      id: 'CON-2024-001',
      vendor: 'Tech Solutions Ltd',
      type: 'Service Agreement',
      value: 2500000,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      status: 'active',
      category: 'Technology'
    },
    {
      id: 'CON-2024-002',
      vendor: 'Global Equipment Co.',
      type: 'Supply Agreement',
      value: 1800000,
      startDate: '2024-02-01',
      endDate: '2025-01-31',
      status: 'active',
      category: 'Equipment'
    },
    {
      id: 'CON-2024-003',
      vendor: 'Safety Gear International',
      type: 'Maintenance Contract',
      value: 450000,
      startDate: '2024-03-01',
      endDate: '2025-02-28',
      status: 'pending',
      category: 'Safety Equipment'
    }
  ];

  // Stats
  const stats = {
    totalVendors: vendors.length,
    activeVendors: vendors.filter(v => v.status === 'active').length,
    totalSpend: vendors.reduce((sum, v) => sum + v.totalSpend, 0),
    avgRating: (vendors.reduce((sum, v) => sum + v.rating, 0) / vendors.length).toFixed(1),
    openRFQs: rfqs.filter(r => r.status === 'open').length,
    activePOs: purchaseOrders.filter(po => po.status === 'in-progress').length,
    activeContracts: contracts.filter(c => c.status === 'active').length
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-600 text-white',
      pending: 'bg-amber-600 text-white',
      'in-progress': 'bg-blue-600 text-white',
      approved: 'bg-green-600 text-white',
      delivered: 'bg-purple-600 text-white',
      open: 'bg-blue-600 text-white',
      evaluating: 'bg-amber-600 text-white',
      awarded: 'bg-green-600 text-white'
    };
    return colors[status] || 'bg-gray-600 text-white';
  };

  const getCategoryColor = (category) => {
    const colors = {
      Technology: 'bg-blue-600 text-white',
      Equipment: 'bg-green-600 text-white',
      'Office Supplies': 'bg-purple-600 text-white',
      'Safety Equipment': 'bg-red-600 text-white'
    };
    return colors[category] || 'bg-gray-600 text-white';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `SAR ${amount.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Procurement Management</h2>
            <p className="text-sm text-gray-500">Vendor management, RFQs, purchase orders, and contracts</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New {activeSubTab === 'vendors' ? 'Vendor' : 
                 activeSubTab === 'rfqs' ? 'RFQ' :
                 activeSubTab === 'purchase-orders' ? 'PO' : 'Contract'}
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Vendors', value: stats.totalVendors, icon: Users, color: 'blue' },
            { label: 'Active Vendors', value: stats.activeVendors, icon: CheckCircle2, color: 'green' },
            { label: 'Total Spend', value: formatCurrency(stats.totalSpend), icon: DollarSign, color: 'emerald' },
            { label: 'Avg Rating', value: `${stats.avgRating}/5`, icon: Star, color: 'amber' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="flex items-center p-4 bg-gray-50 rounded-lg"
            >
              <div className={`p-2 rounded-lg bg-${stat.color}-600 text-white mr-3`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSubTab === tab.id
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={`Search ${activeSubTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Filter className="w-4 h-4" />
            </button>
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content based on active sub tab */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {activeSubTab === 'vendors' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Financial
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{vendor.name}</div>
                          <div className="text-sm text-gray-500">{vendor.id}</div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(vendor.category)}`}>
                            {vendor.category}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900">{vendor.contactPerson}</div>
                        <div className="text-sm text-gray-500">{vendor.email}</div>
                        <div className="text-sm text-gray-500">{vendor.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Rating</span>
                          <div className="flex items-center">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-sm font-medium ml-1">{vendor.rating}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">On-time</span>
                          <span className="text-sm font-medium">{vendor.performance.onTimeDelivery}%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Quality</span>
                          <span className="text-sm font-medium">{vendor.performance.qualityScore}%</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(vendor.totalSpend)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {vendor.contracts} contracts
                        </div>
                        <div className="text-sm text-gray-500">
                          {vendor.activePOs} active POs
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(vendor.status)}`}>
                        {vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeSubTab === 'rfqs' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RFQ Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Responses
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deadline
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rfqs.map((rfq) => (
                  <tr key={rfq.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{rfq.title}</div>
                        <div className="text-sm text-gray-500">{rfq.id}</div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(rfq.category)}`}>
                          {rfq.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(rfq.budget)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{rfq.responses} responses</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(rfq.deadline)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(rfq.status)}`}>
                        {rfq.status.charAt(0).toUpperCase() + rfq.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeSubTab === 'purchase-orders' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PO Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delivery
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchaseOrders.map((po) => (
                  <tr key={po.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{po.id}</div>
                        <div className="text-sm text-gray-500">{po.items} items</div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(po.category)}`}>
                          {po.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{po.vendor}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(po.totalAmount)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(po.expectedDelivery)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
                        {po.status.charAt(0).toUpperCase() + po.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeSubTab === 'contracts' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contract Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{contract.id}</div>
                        <div className="text-sm text-gray-500">{contract.type}</div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(contract.category)}`}>
                          {contract.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{contract.vendor}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(contract.value)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(contract.startDate)} - {formatDate(contract.endDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                        {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcurementTab; 